import React, { useEffect } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { Resource } from "@mapmycustomers/shared/types/entity";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import { Alert, Divider } from "@mapmycustomers/ui";

import DraftRecipientsView from "@app/component/email/creation/EmailDraftsModal/DraftRecipientsView";
import EmailBodyView from "@app/component/email/EmailBodyView";
import { updateEmailDraft } from "@app/store/email/actions";
import EmailDraftInfo from "@app/types/email/EmailDraftInfo";
import EmailEntityOpenCallback from "@app/types/email/EmailEntityOpenCallback";

import styles from "./DraftView.module.scss";
import messages from "./messages";

export interface DraftViewProps {
  draftInfo: EmailDraftInfo;
  onEntityOpen: EmailEntityOpenCallback;
}

const DraftView = ({
  draftInfo: { draft, entities, resources },
  onDraftUpdate,
  onEntityOpen,
}: DraftViewProps & typeof mapDispatchToProps) => {
  const intl = useIntl();

  const [resourcesRemovedAlertVisible, showResourcesRemovedAlert, hideResourcedRemovedAlert] =
    useBoolean();

  useEffect(() => {
    hideResourcedRemovedAlert();
  }, [draft.id, hideResourcedRemovedAlert]);

  const handleInvalidResourceLinksRemoved = useDynamicCallback(
    (value: string, resources: Resource[]) => {
      onDraftUpdate({
        attachments: draft.attachments,
        callback: showResourcesRemovedAlert,
        draft: {
          ...draft,
          content: value,
        },
        entities,
        resources,
      });
    }
  );

  return (
    <div className={styles.container}>
      <div className={styles.recipients}>
        <span>{intl.formatMessage(messages.toLabel)}&nbsp;</span>
        <DraftRecipientsView
          entities={entities}
          entityType={draft.metadata.recipientType}
          onEntityOpen={onEntityOpen}
          recipients={draft.metadata.recipients.to}
        />
      </div>
      <Divider className={styles.divider} />
      <EmailBodyView
        attachments={draft.attachments}
        body={draft.content}
        entities={entities}
        entityType={draft.metadata.recipientType}
        onInvalidResourceLinksRemoved={handleInvalidResourceLinksRemoved}
        removeResourcesAlertSuffix={intl.formatMessage(messages.removeInvalidResourcesAlertSuffix)}
        resources={resources}
        resourcesMetadata={draft.metadata.resources}
      />
      {resourcesRemovedAlertVisible && (
        <Alert
          className={styles.alert}
          closable
          message={intl.formatMessage(messages.resourcesRemoved)}
          onClose={hideResourcedRemovedAlert}
          showIcon
          type="success"
        />
      )}
    </div>
  );
};

const mapDispatchToProps = {
  onDraftUpdate: updateEmailDraft.request,
};

export default connect(null, mapDispatchToProps)(DraftView);
